<script setup lang="ts">
/** "ประวัติ": the linked POS customer's orders + open packs (POS chat_v1), and orders saved from this chat. */
import { computed, ref, watch } from 'vue'
import { getPosApi } from '../../api/pos'
import type { PosHistoryOrder, PosOpenPack, PosOrderStatus } from '../../api/pos/types'
import type { ConversationDetail } from '../../api/types'
import { baht } from '../../lib/inbox'
import { deliveryDateLabel, historyStats, slotLabel } from '../../lib/order'
import { useInbox } from '../../stores/inbox'
import { useOrders } from '../../stores/order'
import Icon from '../ui/Icon.vue'
import '../../styles/order.css'

const props = defineProps<{ detail: ConversationDetail }>()
const inbox = useInbox()
const orders = useOrders()

const id = computed(() => props.detail.id)
watch(
  id,
  (v) => {
    orders.form(v)
    void orders.open(v)
  },
  { immediate: true },
)

const ctx = computed(() => orders.contexts[id.value] ?? null)
const cid = computed(() => orders.forms[id.value]?.customerId ?? ctx.value?.customer_id ?? null)
const hist = computed(() => (cid.value ? (orders.histories[cid.value] ?? null) : null))
const data = computed(() => hist.value?.data ?? null)
const customer = computed(() => (cid.value ? (orders.customers[cid.value] ?? data.value?.customer ?? null) : null))
const stats = computed(() => historyStats(data.value?.orders ?? []))
const unsettled = computed(() => (ctx.value?.chat_orders ?? []).filter((o) => o.status === 'unknown' || o.status === 'pending'))

const details = ref<Record<number, { state: 'loading' | 'ok' | 'error'; data: PosOrderStatus | null; error: string | null }>>({})

async function toggle(o: PosHistoryOrder): Promise<void> {
  const k = o.order_delivery_id
  if (details.value[k]) {
    delete details.value[k]
    return
  }
  details.value[k] = { state: 'loading', data: null, error: null }
  try {
    details.value[k] = { state: 'ok', data: await getPosApi().orderStatus(String(k)), error: null }
  } catch (e) {
    details.value[k] = { state: 'error', data: null, error: e instanceof Error ? e.message : String(e) }
  }
}

/** The chat has a held order (or an edit is open): the order tab shows it and says what to press first. */
function heldFirst(): boolean {
  const why = orders.fillBlock(id.value)
  if (!why) return false
  inbox.panelTab = 'order'
  inbox.flash(why)
  return true
}

async function repeat(o: PosHistoryOrder): Promise<void> {
  if (heldFirst()) return
  inbox.panelTab = 'order'
  const found = await orders.repeat(id.value, o)
  if (!found) inbox.flash('แพ็กของออเดอร์นี้ปิดขายแล้ว เลือกแพ็กใหม่ที่แท็บลงออเดอร์')
}

function cont(p: PosOpenPack): void {
  if (heldFirst()) return
  orders.startContinue(id.value, p)
  inbox.panelTab = 'order'
}

function statusTone(o: { status: string }): string {
  if (o.status === '-1') return 'st-x'
  if (o.status === '4') return 'st-ok'
  return 'st-wait'
}

const chatStatus: Record<string, string> = {
  pending: 'กำลังบันทึก',
  unknown: 'POS ยังไม่ยืนยัน',
  rejected: 'POS ไม่รับ',
  done: 'บันทึกแล้ว',
}
</script>

<template>
  <div class="pane ord" role="tabpanel" aria-label="ประวัติสั่งซื้อ">
    <div class="pn-body scroll">
      <section v-if="unsettled.length" class="cs">
        <div class="sec-h"><h3 class="sec-t">ลงจากแชทนี้ ยังไม่เสร็จ</h3></div>
        <div v-for="o in unsettled" :key="o.id" class="un">
          <span class="chip warn"><Icon name="alert" />{{ chatStatus[o.status] }}</span>
          <span class="note">{{ o.error_message ?? 'กดบันทึกอีกครั้งที่แท็บลงออเดอร์ได้ ระบบจะไม่ลงซ้ำ' }}</span>
        </div>
      </section>

      <template v-if="!cid">
        <div class="empty">
          <p class="pane-note">ยังไม่ได้ผูกแชทนี้กับลูกค้าใน POS จึงยังไม่มีประวัติสั่งซื้อ</p>
          <button class="btn-s" @click="inbox.panelTab = 'order'"><Icon name="user" />เลือกลูกค้าที่แท็บลงออเดอร์</button>
        </div>
      </template>

      <template v-else>
        <section class="cs">
          <div class="sec-h">
            <h3 class="sec-t">สรุปการสั่งซื้อ</h3>
            <span class="grow"></span>
            <span v-if="data" class="sec-s">{{ data.orders.length >= 20 ? 'จาก 20 ใบล่าสุด' : `ทั้งหมด ${data.orders.length} ใบ` }}</span>
          </div>
          <div v-if="customer" class="hc">
            <span class="hc-n">{{ customer.name }}</span>
            <span v-if="customer.mobile_masked" class="mono">{{ customer.mobile_masked }}</span>
            <span class="grow"></span>
            <span class="mono hc-id">POS #{{ customer.id }}</span>
          </div>
          <p v-if="hist?.state === 'error'" class="err">
            <Icon name="alertCircle" /><span>โหลดประวัติจาก POS ไม่สำเร็จ: {{ hist.error }}</span>
            <button class="tlink" @click="orders.loadHistory(cid)">ลองใหม่</button>
          </p>
          <p v-else-if="!data" class="note"><span class="spin"></span> กำลังโหลดประวัติ</p>
          <div v-if="data" class="stats">
            <div class="st"><span class="st-l">สั่งมาแล้ว</span><span class="st-v">{{ stats.count }} ครั้ง</span></div>
            <div class="st"><span class="st-l">ยอดรวม</span><span class="st-v mono">{{ baht(stats.total) }}</span></div>
            <div class="st"><span class="st-l">เฉลี่ยต่อครั้ง</span><span class="st-v mono">{{ baht(stats.avg) }}</span></div>
          </div>
        </section>

        <section v-if="data?.open_packs.length" class="cs">
          <div class="sec-h"><h3 class="sec-t">แพ็กที่ยังเหลือสิทธิ์</h3></div>
          <article v-for="p in data.open_packs" :key="p.order_header_id" class="pk-c">
            <div class="pk-1">
              <span class="pk-n">{{ p.set_menu_name }}</span>
              <span class="grow"></span>
              <button class="cont-b" :aria-label="`สั่งต่อ ${p.set_menu_name} ไปที่แท็บลงออเดอร์`" @click="cont(p)"><Icon name="repeat" />สั่งต่อ</button>
            </div>
            <div class="pk-2">
              <span class="cnt">เหลือ {{ p.meals_remaining }}/{{ p.meals_quota }} มื้อ</span>
              <span>ส่งแล้ว {{ p.rounds_used }}/{{ p.rounds }} รอบ</span>
            </div>
          </article>
        </section>

        <section v-if="data" class="cs last">
          <div class="sec-h"><h3 class="sec-t">ออเดอร์ย้อนหลัง</h3><span class="sec-s">ใหม่สุดก่อน</span></div>
          <p v-if="!data.orders.length" class="sec-s">ยังไม่มีออเดอร์</p>
          <div class="ocs">
            <article v-for="(o, i) in data.orders" :key="o.order_delivery_id" class="oc" :class="{ 'is-last': i === 0 }">
              <div class="oc-1">
                <span class="oc-no mono">{{ o.order_code }}</span>
                <span class="dotsep"></span>
                <span>ส่ง {{ deliveryDateLabel(o.delivery_date) }}</span>
                <span v-if="i === 0" class="new">ล่าสุด</span>
                <span class="grow"></span>
                <span class="oc-p mono">{{ baht(o.total) }}</span>
              </div>
              <div class="oc-pk">{{ o.set_menu_name }}</div>
              <div class="oc-3">
                <span class="stc" :class="statusTone(o)"><Icon :name="o.status === '-1' ? 'x' : 'check'" />{{ o.status_text }}</span>
                <span v-if="o.source === 'chat'" class="chip">จากแชท</span>
                <span class="oc-m">{{ o.meals }} มื้อ · {{ o.carrier === 'dhl' ? 'DHL' : 'Lalamove' }}</span>
                <span class="grow"></span>
                <button class="tlink" :aria-expanded="!!details[o.order_delivery_id]" @click="toggle(o)">
                  {{ details[o.order_delivery_id] ? 'ซ่อน' : 'รายละเอียด' }}
                </button>
                <button class="rep" :aria-label="`สั่งซ้ำออเดอร์ ${o.order_code} ไปที่แท็บลงออเดอร์`" @click="repeat(o)"><Icon name="repeat" />สั่งซ้ำ</button>
              </div>
              <div v-if="details[o.order_delivery_id]" class="od">
                <p v-if="details[o.order_delivery_id]?.state === 'loading'" class="note"><span class="spin"></span> กำลังโหลด</p>
                <p v-else-if="details[o.order_delivery_id]?.state === 'error'" class="err">
                  <Icon name="alertCircle" /><span>{{ details[o.order_delivery_id]?.error }}</span>
                </p>
                <template v-else-if="details[o.order_delivery_id]?.data">
                  <div v-for="l in details[o.order_delivery_id]?.data?.lines ?? []" :key="l.menu_id" class="od-l">
                    <span>{{ l.name }}</span><span class="mono">×{{ l.qty }}</span>
                  </div>
                  <div class="od-l sum">
                    <span
                      >ค่าส่ง {{ baht(details[o.order_delivery_id]?.data?.totals.shipping_fee ?? 0) }}
                      <template v-if="details[o.order_delivery_id]?.data?.totals.discount"
                        >· ส่วนลด {{ baht(details[o.order_delivery_id]?.data?.totals.discount ?? 0) }}</template
                      ></span
                    >
                    <span>{{ slotLabel(orders.catalog, details[o.order_delivery_id]?.data?.delivery.time ?? null) }}</span>
                  </div>
                  <a
                    v-if="details[o.order_delivery_id]?.data?.bill_url"
                    class="tlink ac"
                    :href="details[o.order_delivery_id]?.data?.bill_url ?? undefined"
                    target="_blank"
                    rel="noopener noreferrer"
                    ><Icon name="external" />เปิดบิล</a
                  >
                </template>
              </div>
            </article>
          </div>
        </section>
      </template>
    </div>
  </div>
</template>

<style scoped>
.empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding-bottom: 24px;
}
.un {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 4px 8px;
}
.hc {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px 8px;
  font-size: 13px;
  line-height: 20px;
}
.hc-n {
  font-weight: 600;
}
.hc .mono {
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
.pk-c {
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 10px 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.pk-1 {
  display: flex;
  align-items: center;
  gap: 8px;
}
.pk-n {
  font-size: 13.5px;
  font-weight: 600;
  line-height: 21px;
}
.pk-2 {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 10px;
  font-size: 12.5px;
  line-height: 19px;
  color: var(--mut);
}
.cont-b,
.rep {
  height: 30px;
  padding: 0 11px 0 9px;
  border-radius: 8px;
  border: 1px solid #bfdccd;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12.5px;
  line-height: 19px;
  font-weight: 600;
  white-space: nowrap;
  background: #fff;
  color: var(--ac2);
  flex: none;
}
.cont-b:hover,
.rep:hover {
  background: var(--acs);
}
.cont-b .ic,
.rep .ic {
  width: 14px;
  height: 14px;
  stroke-width: 2;
}
.ocs {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.oc {
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: #fff;
}
.oc.is-last {
  border-color: #cfe3d8;
  background: #f7fbf9;
}
.oc-1 {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 22px;
  white-space: nowrap;
  font-size: 12.5px;
  line-height: 20px;
  color: var(--mut);
}
.oc-no {
  font-size: 12px;
  font-weight: 500;
}
.new {
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 0 7px;
  border-radius: 999px;
  background: var(--ac);
  color: #fff;
  font-size: 12px;
  line-height: 18px;
  font-weight: 600;
}
.oc-p {
  font-size: 15px;
  font-weight: 600;
  color: var(--ink);
}
.oc-pk {
  font-size: 13.5px;
  font-weight: 600;
  line-height: 21px;
}
.oc-3 {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 8px;
  min-height: 32px;
  margin-top: 2px;
}
.oc-m {
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
  white-space: nowrap;
}
.stc {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 22px;
  padding: 1px 8px 1px 6px;
  border-radius: 6px;
  font-size: 12px;
  line-height: 18px;
  font-weight: 500;
  white-space: nowrap;
}
.stc .ic {
  width: 13px;
  height: 13px;
  stroke-width: 2.2;
}
.st-ok {
  background: #e3f3ea;
  color: #0a5e47;
}
.st-wait {
  background: var(--warns);
  color: #7a4700;
}
.st-x {
  background: var(--crits);
  color: #8c1d14;
}
.od {
  margin-top: 6px;
  padding-top: 8px;
  border-top: 1px dashed #e3eae6;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.od-l {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 12.5px;
  line-height: 19px;
}
.od-l .mono {
  color: var(--mut);
}
.od-l.sum {
  margin-top: 4px;
  color: var(--mut);
}
.od .tlink {
  align-self: flex-start;
  text-decoration: none;
}
</style>
